import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Game, STATE } from './engine/game';
import { Renderer } from './renderer';
import { computeLayout } from './layout';

const KEYMAP = {
  ArrowLeft: 'left',
  ArrowRight: 'right',
  ArrowDown: 'soft',
  ArrowUp: 'cw',
  KeyZ: 'ccw',
  KeyX: 'cw',
  KeyC: 'hold',
  ShiftLeft: 'hold',
  ShiftRight: 'hold',
  Space: 'hard',
  KeyP: 'pause',
  KeyR: 'restart',
  Enter: 'start',
};

const PREVENT = new Set(['Space', 'ArrowLeft', 'ArrowRight', 'ArrowDown', 'ArrowUp']);

const overlayCopy = (state, hud) => {
  if (state === STATE.PAUSED) {
    return { title: 'Paused', subtitle: 'Take your time.', action: 'pause', button: 'Resume' };
  }
  if (state === STATE.GAMEOVER) {
    return { title: 'Game Over', subtitle: `Score ${hud.score} · ${hud.lines} lines`, action: 'start', button: 'Play Again' };
  }
  return { title: 'Pink Drop', subtitle: 'Stack lines, clear them, keep the streak alive.', action: 'start', button: 'Start' };
};

const TouchButton = ({ action, label, game, children }) => {
  const press = (e) => {
    e.preventDefault();
    game.current?.input(action, true);
  };
  const release = (e) => {
    e.preventDefault();
    game.current?.input(action, false);
  };
  return (
    <button
      type="button"
      onPointerDown={press}
      onPointerUp={release}
      onPointerLeave={release}
      onPointerCancel={release}
      onContextMenu={(e) => e.preventDefault()}
      className="flex-1 h-14 rounded-xl bg-white/10 active:bg-pink-500/60 border border-white/10 text-white font-bold text-lg backdrop-blur-sm touch-none"
      aria-label={label}
    >
      {children}
    </button>
  );
};

const PinkDrop = () => {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const gameRef = useRef(null);
  const layoutRef = useRef(null);
  const touchRef = useRef(null);

  const [layout, setLayout] = useState(null);
  const [hud, setHud] = useState({
    score: 0,
    lines: 0,
    level: 1,
    combo: 0,
    state: STATE.READY,
    next: [],
    hold: null,
  });

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return undefined;

    const game = new Game();
    gameRef.current = game;
    let renderer;
    try {
      renderer = new Renderer(canvas);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Pink Drop: WebGL init failed', err);
      return undefined;
    }

    const size = { w: 1, h: 1 };
    let raf = 0;
    let last = performance.now();
    let lastVersion = -1;

    const measure = () => {
      const rect = wrap.getBoundingClientRect();
      if (rect.width < 1 || rect.height < 1) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      size.w = rect.width;
      size.h = rect.height;
      renderer.resize(rect.width, rect.height, dpr);
      const next = computeLayout(rect.width, rect.height);
      layoutRef.current = next;
      setLayout(next);
    };

    const ro = new ResizeObserver(measure);
    ro.observe(wrap);
    measure();

    const loop = (t) => {
      const dt = Math.min(t - last, 80);
      last = t;
      game.update(dt);
      if (game.version !== lastVersion) {
        lastVersion = game.version;
        setHud(game.getHud());
      }
      if (layoutRef.current) renderer.draw(game, layoutRef.current, t / 1000);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      gameRef.current = null;
    };
  }, []);

  useEffect(() => {
    const down = (e) => {
      const action = KEYMAP[e.code];
      if (!action) return;
      if (PREVENT.has(e.code)) e.preventDefault();
      if (e.repeat) return;
      gameRef.current?.input(action, true);
    };
    const up = (e) => {
      const action = KEYMAP[e.code];
      if (!action) return;
      gameRef.current?.input(action, false);
    };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
    };
  }, []);

  // --- touch gestures ------------------------------------------------------
  // Horizontal drag moves, a gentle downward drag soft-drops a row at a time,
  // a sharp downward flick hard-drops, and an upward drag holds the piece.
  const STEP = 26;
  const FLICK_V = 1.4; // px per ms
  const FLICK_DY_MIN = 14; // ignore tiny events when classifying a flick
  const FLICK_DIST = 44;
  const HOLD_UP = 44;

  const onTouchStart = useCallback((e) => {
    if (e.touches.length !== 1) return;
    const t = e.touches[0];
    const now = Date.now();
    touchRef.current = {
      startX: t.clientX,
      startY: t.clientY,
      startTime: now,
      lastX: t.clientX,
      lastY: t.clientY,
      lastTime: now,
      accX: 0,
      accY: 0,
      moved: false,
      held: false,
      dropped: false,
    };
  }, []);

  const onTouchMove = useCallback((e) => {
    const game = gameRef.current;
    const g = touchRef.current;
    if (!game || !g) return;
    const t = e.touches[0];
    const now = Date.now();
    const dt = Math.max(1, now - g.lastTime);
    const dx = t.clientX - g.lastX;
    const dy = t.clientY - g.lastY;

    // Horizontal: proportional stepping.
    g.accX += dx;
    while (g.accX >= STEP) {
      game.input('right', true);
      game.input('right', false);
      g.accX -= STEP;
      g.moved = true;
    }
    while (g.accX <= -STEP) {
      game.input('left', true);
      game.input('left', false);
      g.accX += STEP;
      g.moved = true;
    }

    if (!g.dropped) {
      if (dy > 0) {
        const totalDown = t.clientY - g.startY;
        const velocity = dy / dt;
        if (velocity > FLICK_V && dy > FLICK_DY_MIN && totalDown > FLICK_DIST) {
          // Sharp downward flick => hard drop.
          game.input('hard', true);
          game.input('hard', false);
          g.dropped = true;
          g.moved = true;
        } else {
          // Gentle downward drag => move down one row per step.
          g.accY += dy;
          while (g.accY >= STEP) {
            game.input('down', true);
            game.input('down', false);
            g.accY -= STEP;
            g.moved = true;
          }
        }
      } else if (dy < 0) {
        g.accY += dy;
        if (!g.held && g.startY - t.clientY > HOLD_UP) {
          game.input('hold', true);
          game.input('hold', false);
          g.held = true;
          g.moved = true;
        }
      }
    }

    g.lastX = t.clientX;
    g.lastY = t.clientY;
    g.lastTime = now;
  }, []);

  const onTouchEnd = useCallback((e) => {
    const game = gameRef.current;
    const g = touchRef.current;
    const onButton = e && e.target && e.target.closest && e.target.closest('button');
    if (game && g && !onButton && !g.moved && !g.held && !g.dropped && Date.now() - g.startTime < 260) {
      game.input('cw', true);
      game.input('cw', false);
    }
    touchRef.current = null;
  }, []);

  const s = layout ? layout.scale : 16;
  const labelStyle = {
    fontSize: Math.max(7, s * 0.24),
    letterSpacing: Math.max(0.5, s * 0.05),
  };
  const overlay = overlayCopy(hud.state, hud);
  const showOverlay = hud.state !== STATE.PLAYING;

  const onOverlayAction = () => {
    gameRef.current?.input(overlay.action === 'pause' ? 'pause' : 'start', true);
    gameRef.current?.input(overlay.action === 'pause' ? 'pause' : 'start', false);
  };

  return (
    <div
      ref={wrapRef}
      className="relative w-full h-full overflow-hidden select-none"
      style={{ touchAction: 'none' }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full block" />

      {layout && (
        <div className="absolute inset-0 pointer-events-none font-mono">
          <div
            className="absolute flex flex-col items-start"
            style={{ left: layout.hold.x, top: layout.hold.y, width: layout.hold.w, height: layout.hold.h, padding: s * 0.16 }}
          >
            <span className="uppercase text-pink-300/70" style={labelStyle}>Hold</span>
          </div>

          <div
            className="absolute flex flex-col justify-center"
            style={{ left: layout.score.x, top: layout.score.y, width: layout.score.w, height: layout.score.h, paddingLeft: s * 0.16 }}
          >
            <span className="uppercase text-pink-300/70" style={labelStyle}>Score</span>
            <span className="text-white font-bold tabular-nums" style={{ fontSize: s * 0.85, lineHeight: 1.05 }}>
              {hud.score}
            </span>
            <div className="flex text-pink-200/80" style={{ gap: s * 0.35, fontSize: Math.max(7, s * 0.26) }}>
              <span>LVL <b className="text-white">{hud.level}</b></span>
              <span>LINES <b className="text-white">{hud.lines}</b></span>
            </div>
          </div>

          <div
            className="absolute flex flex-col items-start"
            style={{ left: layout.next[0].x, top: layout.next[0].y, width: layout.next[0].w, height: layout.next[0].h, padding: s * 0.16 }}
          >
            <span className="uppercase text-pink-300/70" style={labelStyle}>Next</span>
          </div>

          {hud.combo > 1 && (
            <div
              className="absolute text-pink-300 font-bold uppercase"
              style={{ left: layout.board.x, top: layout.board.y - s * 0.1, fontSize: Math.max(9, s * 0.36), transform: 'translateY(-100%)' }}
            >
              Combo ×{hud.combo}
            </div>
          )}
        </div>
      )}

      {showOverlay && (
        <div className="absolute inset-0 flex items-center justify-center p-4">
          <div className="pointer-events-auto bg-black/75 backdrop-blur-sm border border-pink-500/40 rounded-2xl px-8 py-6 text-center shadow-2xl">
            <h2 className="font-kreon text-2xl text-white mb-1">{overlay.title}</h2>
            <p className="text-pink-200/80 text-sm mb-4">{overlay.subtitle}</p>
            <button
              type="button"
              onClick={onOverlayAction}
              className="px-6 py-3 bg-pink-600 hover:bg-pink-700 active:scale-95 text-white font-bold rounded-xl transition-all shadow-lg"
            >
              {overlay.button}
            </button>
            <p className="hidden md:block text-gray-400 text-xs mt-4">
              ← → move · ↑ rotate · ↓ soft drop · Space hard drop · C hold · P pause
            </p>
            <p className="md:hidden text-gray-400 text-xs mt-4">
              Drag to move · Tap to rotate · Drag up to hold · Flick down to drop
            </p>
          </div>
        </div>
      )}

      {/* Mobile controls */}
      <div className="md:hidden absolute bottom-0 inset-x-0 p-3 flex items-end gap-2 pointer-events-none">
        <div className="pointer-events-auto flex gap-2 flex-1">
          <TouchButton action="left" label="Move left" game={gameRef}>◀</TouchButton>
          <TouchButton action="down" label="Soft drop" game={gameRef}>▼</TouchButton>
          <TouchButton action="right" label="Move right" game={gameRef}>▶</TouchButton>
          <TouchButton action="cw" label="Rotate" game={gameRef}>⟳</TouchButton>
          <TouchButton action="hard" label="Hard drop" game={gameRef}>⤓</TouchButton>
          <TouchButton action="hold" label="Hold" game={gameRef}>H</TouchButton>
          <TouchButton action="pause" label="Pause" game={gameRef}>⏸</TouchButton>
        </div>
      </div>
    </div>
  );
};

export default PinkDrop;
